import { useAuth } from "@clerk/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { RdTimeBlockCreateResponse } from "../../../shared/api-types.js";
import { ApiRequestError, authenticatedApi } from "./api.js";
import {
  blockTimeApiPath,
  blockTimeRequestBody,
  blockTimeSaveFailure,
  type BlockTimeForm,
  type BlockTimeSaveFailure,
} from "./rd-block-time.js";
import { RD_SCHEDULE_WEEK_QUERY_PREFIX } from "./rd-schedule-week.js";

export function failureOf(error: unknown): BlockTimeSaveFailure {
  if (error instanceof ApiRequestError) {
    return blockTimeSaveFailure({ status: error.status, body: error.body });
  }

  return blockTimeSaveFailure({ status: null, body: undefined });
}

/**
 * NO-877: POST /api/rd-schedule/blocks. A save refreshes the Week reads, so the
 * new block shows on every day it repeats to without a reload. `rdId` names the
 * RD a supervisor is viewing; an RD blocks their own time.
 */
export function useCreateRdTimeBlock(rdId: string | null) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (form: BlockTimeForm) =>
      authenticatedApi<{ data: RdTimeBlockCreateResponse }>(
        getToken,
        blockTimeApiPath(rdId),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(blockTimeRequestBody(form)),
        }
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: RD_SCHEDULE_WEEK_QUERY_PREFIX }),
  });
}
